import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { NotificationTypeEnum } from '@shared/enum/notification-type.enum';
import { NotificationsModel } from '@shared/models/notifications.model';
import { BranchModel } from '@shared/models/branch.model';
import { branchSupportsOrders } from '@shared/utils/branch-orders-available.util';
import { OrderService } from '@shared/services/order/order.service';
import { first } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class HandleNotificationClickService {
  constructor(private router: Router, private orderService: OrderService) {}

  /** Matches `new-table` routes under branch: `/tables/:restaurantId/branches/:branchId/:table_id`. */
  navigateToTableDetails(tableId: string, extras?: NavigationExtras): void {
    const restaurant = JSON.parse(localStorage.getItem('restaurant') ?? 'null') as { id?: string } | null;
    const branch = JSON.parse(localStorage.getItem('branch') ?? 'null') as { id?: string } | null;
    const rid = restaurant?.id;
    const bid = branch?.id;
    if (rid && bid) {
      void this.router.navigate(['tables', rid, 'branches', bid, tableId], extras);
    } else {
      void this.router.navigate(['tables', tableId], extras);
    }
  }

  /** API / FCM may send numeric types as strings; normalize before comparing. */
  private notificationType(notification: Pick<NotificationsModel, 'data'>): number {
    return Number(notification?.data?.type);
  }

  openOrderNotificationInTableDetails(notification: Pick<NotificationsModel, 'data'>): void {
    const data = notification.data;
    if (data?.table_id) {
      this.navigateToTableDetails(String(data.table_id), { queryParams: { source: 'panel' } });
      return;
    }

    if (data?.order_id) {
      this.orderService
        .getOrderByID(data.order_id)
        .pipe(first())
        .subscribe({
          next: response => {
            const tableId = response.data?.table?.id;
            if (tableId) {
              this.navigateToTableDetails(String(tableId), { queryParams: { source: 'panel' } });
              return;
            }
            this.handleNotificationClick(notification);
          },
          error: () => this.handleNotificationClick(notification),
        });
      return;
    }

    this.handleNotificationClick(notification);
  }

  handleNotificationClick(notification: Pick<NotificationsModel, 'data'>): void {
    const type = this.notificationType(notification);
    const data = notification.data;

    if (
      type === NotificationTypeEnum.NEW_ORDER ||
      type === NotificationTypeEnum.CANCEL_ORDER ||
      type === NotificationTypeEnum.NOTIFY_WAITER_TO_UPDATE_ORDER ||
      type === NotificationTypeEnum.NOTIFY_WAITER_TO_PAY_CASH
    ) {
      if (data?.order_id) {
        // Order notifications cannot open order details when branch is menu-only
        let branch: BranchModel | null = null;
        try {
          branch = JSON.parse(localStorage.getItem('branch') ?? 'null');
        } catch {
          branch = null;
        }
        if (branchSupportsOrders(branch)) {
          void this.router.navigate(['orders', data.order_id]);
        } else {
          void this.router.navigate(['/menus']);
        }
      }
    } else if (type === NotificationTypeEnum.UNLOCK_RESTAURANT_TABLE || type === NotificationTypeEnum.NOTIFY_WAITER) {
      if (data?.table_id) {
        this.navigateToTableDetails(String(data.table_id), { queryParams: { source: 'panel' } });
      }
    }
  }
}
